import React from "react";
import { scaleLinear } from "d3-scale";
import { useSpring, useSpringRef, useChain, animated } from "@react-spring/web";

import { Group } from "./Group";
import { Header } from "./Header";
import { Footer } from "./Footer";

import { stack } from "../../../utils/stack";

interface StackedBarChartDatum {
  key: string;
  year: number;
  value: number;
  color?: string;
}

interface StackedBarChartProps {
  data: StackedBarChartDatum[];
  title?: string;
  description?: string;
  source?: string;
  sourceLink?: string;
  unit?: string;
  xKey?: string;
  groupKey?: string;
  color?: string;
  width?: number;
  height?: number;
  isAnimated?: boolean;
}

export const StackedBarChart: React.FC<StackedBarChartProps> = ({
  data,
  title,
  description,
  source,
  sourceLink,
  unit = "",
  width = 210,
  height = 280,
  isAnimated = true,
}) => {
  const margin = { top: 80, right: 0, bottom: 0, left: 0 };
  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;

  const groupValues = [...new Set(data.map((d) => d.year))];
  const groupedData = groupValues.map((group) =>
    data.filter((d) => d.year === group)
  );

  const x = scaleLinear().domain([0, 1]).range([0, innerWidth]);
  const y = scaleLinear()
    .domain([0, groupedData.length])
    .range([0, innerHeight]);

  const stackedData = groupedData.map((group) => stack(group));

  const animatedData = stackedData.map((group) =>
    group.map((datum) => {
      const barRef = useSpringRef();
      const barAnimation = useSpring({
        config: {
          duration: isAnimated ? 750 : 0,
        },
        x: isAnimated ? x(datum.startValue) : 0,
        width: isAnimated ? x(datum.relativeValue) + 1 : 0,
        ref: barRef
      });

      const labelRef = useSpringRef();
      const labelAnimation = useSpring({
        config: {
          duration: isAnimated ? 250 : 0,
        },
        opacity: isAnimated ? 1 : 0,
        ref: labelRef,
      });

      useChain([barRef, labelRef], [0, 0.75]);

      return {
        barAnimation,
        labelAnimation,
        ...datum,
      };
    })
  );


  return (
    <>
      {title && <Header title={title} description={description} />}
      {source && (
        <Footer
          text={`Quelle: ${source}`}
          link={sourceLink}
          alignRight={true}
          transform={`translate(${width}, ${height})`}
        />
      )}
      <Group transform={`translate(${margin.left}, ${margin.top})`}>
        {animatedData.map((group, groupIndex) => (
          <Group key={groupIndex} transform={`translate(0, ${y(groupIndex)})`}>
            <text
              fontFamily="TheMixExtraBold, sans-serif"
              fontWeight="700"
              fontSize="14"
              dy="-0.5em"
            >
              {group[0].year}
            </text>
            {group.map((datum, datumIndex) => (
              <React.Fragment key={datumIndex}>
                <animated.rect
                  {...datum.barAnimation}
                  y="0"
                  height="30"
                  fill={datum.color}
                />
                <animated.text
                  {...datum.labelAnimation}
                  x={x(datum.startValue + datum.relativeValue / 2)}
                  fontSize="10"
                  textAnchor="middle"
                  fill="white"
                  dy="19"
                >
                  {`${Math.round(datum.value)} ${
                    datum.relativeValue > 0.15 ? unit : ""
                  }`}
                </animated.text>
              </React.Fragment>
            ))}
          </Group>
        ))}
      </Group>
    </>
  );
};
